import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { authenticateTeam } from '../lib/supabase'

interface Team {
  id: string
  name: string
  slack_team_id: string
}

const TeamLogin: React.FC = () => {
  const [teamId, setTeamId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [availableTeams, setAvailableTeams] = useState<Team[]>([])
  const [loadingTeams, setLoadingTeams] = useState(true)
  const navigate = useNavigate()

  // Load available teams on component mount
  useEffect(() => {
    const loadTeams = async () => {
      try {
        setLoadingTeams(true)
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/dashboard-auth`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          setAvailableTeams(data.teams || [])
        }
      } catch (err) {
        console.error('Failed to load teams:', err)
      } finally {
        setLoadingTeams(false)
      }
    }

    loadTeams()
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!teamId.trim()) {
      setError('Please enter your Team ID')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Try to authenticate the team to verify it exists
      const authResult = await authenticateTeam(teamId.trim())
      
      if (authResult.success && authResult.team) {
        // Redirect to team-specific dashboard
        navigate(`/dashboard/${authResult.team.id}`)
      } else {
        setError('Team not found or access denied')
      }
    } catch (err) {
      console.error('Login error:', err)
      setError('Failed to authenticate team. Please check your Team ID.')
    } finally {
      setLoading(false)
    }
  }

  const handleTeamSelect = (team: Team) => {
    navigate(`/dashboard/${team.id}`)
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">SyncSimple</h1>
          <p className="text-gray-600">Access your team's standup dashboard</p>
        </div>

        {/* Available Teams Section */}
        {!loadingTeams && availableTeams.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Available Teams</h2>
            <div className="space-y-2">
              {availableTeams.map((team) => (
                <button
                  key={team.id}
                  onClick={() => handleTeamSelect(team)}
                  className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{team.name}</h3>
                      <p className="text-sm text-gray-500">Slack Team: {team.slack_team_id}</p>
                      <p className="text-xs text-gray-400">ID: {team.id}</p>
                    </div>
                    <div className="text-blue-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500">Click on a team to access its dashboard</p>
            </div>
          </div>
        )}

        {loadingTeams && (
          <div className="mb-8 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500">Loading available teams...</p>
          </div>
        )}

        {/* Manual Team ID Entry */}
        <div className="border-t pt-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Or Enter Team ID Manually</h2>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="teamId" className="block text-sm font-medium text-gray-700 mb-2">
                Team ID
              </label>
              <input
                type="text"
                id="teamId"
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                placeholder="Enter your Team ID or Slack Team ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Accessing...' : 'Access Dashboard'}
            </button>
          </form>
        </div>

        <div className="mt-8 border-t pt-6">
          <div className="text-sm text-gray-600">
            <p className="mb-2"><strong>Don't have your Team ID?</strong></p>
            <p className="mb-2">• Use the <code className="bg-gray-100 px-1 rounded">/standup</code> command in Slack</p>
            <p className="mb-2">• Or ask your team admin for the Team ID</p>
            <p className="text-xs text-gray-500 mt-4">
              Your Team ID can be found in your Slack team settings or provided by your administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TeamLogin 